import { useCallback, useEffect, useRef } from 'react';

interface SoundOptions {
  volume?: number;
}

export const useSound = (soundUrl: string, options: SoundOptions = {}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef<boolean>(false);

  // Cleanup function
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
        isPlayingRef.current = false;
      }
    };
  }, []);

  const play = useCallback(() => {
    try {
      // Don't play if already playing
      if (isPlayingRef.current) {
        return;
      }

      if (!audioRef.current) {
        audioRef.current = new Audio(soundUrl);
        audioRef.current.volume = options.volume || 0.5;
      }

      // Add event listeners to track playing state
      const handlePlay = () => {
        isPlayingRef.current = true;
      };
      const handleEnded = () => {
        isPlayingRef.current = false;
      };

      audioRef.current.addEventListener('play', handlePlay);
      audioRef.current.addEventListener('ended', handleEnded);
      audioRef.current.addEventListener('pause', handleEnded);

      // Reset and play
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        // Usually happens when user hasn't interacted with the page yet
        console.warn('Could not play notification sound:', error);
        isPlayingRef.current = false;
      });

      // Cleanup event listeners
      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('play', handlePlay);
          audioRef.current.removeEventListener('ended', handleEnded);
          audioRef.current.removeEventListener('pause', handleEnded);
        }
      };
    } catch (error) {
      console.warn('Error playing sound:', error);
      isPlayingRef.current = false;
    }
  }, [soundUrl, options.volume]);

  return { play };
};
